import type { ReactNode } from "react";
import Reveal from "@/components/feature/Reveal";
import SlimZeroLpLink from "@/components/feature/SlimZeroLpLink";
import FirstVisitMonitorLink from "@/components/feature/FirstVisitMonitorLink";
import { ConcernList, FeatureList, MenuGroupList, MenuList, TreatmentCategoryList, TreatmentIndexNav, TreatmentOptionsList, TreatmentSection, TreatmentJumpNav } from "./TreatmentSections";
import { FlowSteps, MethodTable, RiskList, SpecTable } from "./TreatmentProcess";
import { TreatmentCta, TreatmentFaq } from "./TreatmentFaqCta";
import TreatmentHero from "./TreatmentHero";
import TreatmentPriceLink from "./TreatmentPriceLink";
import SlimZeroPriceTable from "./SlimZeroPriceTable";
import { treatmentIndexItems, type TreatmentPageData } from "@/mocks/treatmentPages";
import { isPendingValue } from "@/utils/isPendingContent";

/**
 * カテゴリページ共通のテンプレート。
 * optionsSection を渡した場合のみ、特徴と治療メニューの間に任意セクションを差し込む。
 */
export default function TreatmentDetail({
  data,
  optionsSection,
  afterMenuSection,
  monitorLp,
}: {
  data: TreatmentPageData;
  optionsSection?: ReactNode;
  /** 治療メニューの直後に差し込む任意セクション（未指定のカテゴリは従来通り） */
  afterMenuSection?: ReactNode;
  /**
   * カテゴリTOPページ用の新規お試しモニターLP導線（設定したカテゴリのみPRICEセクション内に表示）。
   * 個別施術ページの FirstVisitMonitorLink と同じ「FOR FIRST VISIT」デザインを再利用する。
   * モニター価格は公式サイトに掲載しない（LPへの入口のみ）。
   */
  monitorLp?: { title: string; description: string; label: string; url: string };
}) {
  const priceHref = `/price?tab=${data.priceTab}`;
  // 未確定（「使用機器：確認中」等）は行ごと非表示にする
  const methodItems = data.method?.filter((item) => !isPendingValue(item.value)) ?? [];
  // 施術時間・ダウンタイム等がすべて未確定の場合はセクションごと非表示にする
  const specRows = data.specs.filter((spec) => !isPendingValue(spec.value));
  const baseIndexItems = data.indexItems ?? treatmentIndexItems;
  const indexItems = baseIndexItems.filter(
    (item) =>
      !(item.target === "tr-spec" && specRows.length === 0) &&
      !(item.target === "tr-method" && methodItems.length === 0),
  );

  return (
    <>
      <TreatmentHero data={data} />
      <TreatmentIndexNav items={indexItems} />

      <TreatmentSection id="tr-concerns" en="CONCERNS" jp="このようなお悩みの方へ" tone="tint">
        <ConcernList items={data.concerns} />
      </TreatmentSection>

      <TreatmentSection id="tr-about" en="ABOUT" jp="施術について" tone="plain">
        <div className="max-w-3xl space-y-6">
          {data.about.paragraphs.map((paragraph) => (
            <p key={paragraph} className="text-[14px] leading-[2.1] text-foreground-700 md:text-[15px]">
              {paragraph}
            </p>
          ))}
          {data.about.note ? (
            <p className="border-l-2 border-accent-500 pl-4 font-heading text-[14px] leading-[2] text-foreground-900 md:text-[15px]">
              {data.about.note}
            </p>
          ) : null}
        </div>
      </TreatmentSection>

      <TreatmentSection id="tr-features" en="FEATURES" jp="特徴" tone="tint">
        <FeatureList items={data.features} />
      </TreatmentSection>

      {methodItems.length > 0 ? (
        <TreatmentSection id="tr-method" en="METHOD" jp="施術情報" tone="plain">
          <Reveal>
            <MethodTable items={methodItems} />
          </Reveal>
        </TreatmentSection>
      ) : null}

      {optionsSection}

      {data.treatmentCategories ? (
        <>
          <TreatmentSection
            id="tr-menu"
            en={data.treatmentCategories.en}
            jp={data.treatmentCategories.jp}
            lead={data.treatmentCategories.lead}
            tone="plain"
          >
            <TreatmentCategoryList groups={data.treatmentCategories.categories} />
          </TreatmentSection>

          <TreatmentSection
            id="tr-concern"
            en="FIND BY CONCERN"
            jp={data.menu.title}
            lead={data.menu.lead}
            tone="tint"
          >
            {data.jump ? (
              <div className="mb-12 md:mb-14">
                <TreatmentJumpNav title={data.jump.title} lead={data.jump.lead} items={data.jump.items} />
              </div>
            ) : null}
            {data.menuGroups ? (
              <MenuGroupList groups={data.menuGroups} />
            ) : (
              <MenuList items={data.menu.items} priceHref={priceHref} />
            )}
          </TreatmentSection>
        </>
      ) : data.treatmentOptions ? (
        <TreatmentSection
          id="tr-menu"
          en={data.treatmentOptions.en}
          jp={data.treatmentOptions.jp}
          lead={data.treatmentOptions.lead}
          tone="plain"
        >
          <TreatmentOptionsList items={data.treatmentOptions.items} />
        </TreatmentSection>
      ) : (
        <TreatmentSection id="tr-menu" en="MENU" jp={data.menu.title} lead={data.menu.lead} tone="plain">
          {data.jump ? (
            <div className="mb-12 md:mb-14">
              <TreatmentJumpNav title={data.jump.title} lead={data.jump.lead} items={data.jump.items} />
            </div>
          ) : null}
          {data.menuGroups ? (
            <MenuGroupList groups={data.menuGroups} />
          ) : (
            <MenuList items={data.menu.items} priceHref={priceHref} />
          )}
        </TreatmentSection>
      )}

      {afterMenuSection}

      <TreatmentSection id="tr-flow" en="FLOW" jp="施術の流れ" tone="tint">
        <FlowSteps items={data.flow} />
      </TreatmentSection>

      <TreatmentSection id="tr-price" en="PRICE" jp="料金" tone="plain">
        {data.priceLp ? (
          <div className="space-y-10">
            <SlimZeroPriceTable />
            <SlimZeroLpLink label="Medical Slim ZEROのプラン・詳細を見る" />
          </div>
        ) : (
          <div className="space-y-6 md:space-y-8">
            <TreatmentPriceLink categoryId={data.priceTab} categoryJp={data.jp} />
            {/* カテゴリTOP：定価料金表導線の直後にLP導線（医療脱毛の施術ページと同じ「FOR FIRST VISIT」デザインを再利用） */}
            {monitorLp ? (
              <FirstVisitMonitorLink
                title={monitorLp.title}
                description={monitorLp.description}
                label={monitorLp.label}
                url={monitorLp.url}
              />
            ) : null}
          </div>
        )}
      </TreatmentSection>

      {specRows.length > 0 ? (
        <TreatmentSection id="tr-spec" en="TIME / DOWNTIME" jp="施術時間・ダウンタイム等" tone="tint">
          <SpecTable items={specRows} />
        </TreatmentSection>
      ) : null}

      <TreatmentSection id="tr-risk" en="RISK / NOTES" jp="リスク・副作用・注意事項" tone="plain">
        <RiskList items={data.risks} note={data.riskNote} />
      </TreatmentSection>

      <TreatmentSection id="tr-faq" en="Q&amp;A" jp="よくあるご質問" tone="tint">
        <div className="max-w-3xl">
          <TreatmentFaq items={data.faqs} />
        </div>
      </TreatmentSection>

      <TreatmentCta lineLabel={data.ctaLabel} treatmentJp={data.jp} />
    </>
  );
}